import { useCallback, useRef, useState, type ReactNode } from 'react';
import { Download, FileDown, Upload } from 'lucide-react';
import { useToast } from '@kss/ui';
import { downloadCsv, headerOnlyCsv, parseCsv, rowsToCsv, type CsvColumn } from './csv';
import { useModulePerms } from '../../hooks/useModulePerms';

/**
 * Generic CSV import/export wiring for any list page.
 *
 * Usage:
 * ```tsx
 * const csv = useCsvIO<Store, StoreCreate>({
 *   filename: 'stores',
 *   columns: CSV_COLUMNS,
 *   selected,
 *   rows,
 *   rowToCreate: csvRowToCreate,
 *   bulkCreate: (items) => storeService.create(items),
 *   reload: load,
 * });
 *
 * // In your AddDropdown items:
 * <AddDropdown items={[...newItems, ...csv.menuItems]} />
 * {csv.fileInput}
 * ```
 *
 * One hook replaces ~80 lines of boilerplate per list page (state for the
 * `importing` flag, the file ref, three handlers, and the hidden input).
 */

export interface UseCsvIOConfig<T, C> {
  /** Base filename without extension. */
  filename: string;
  columns: CsvColumn<T>[];
  rows: T[];
  /** Row IDs the user has selected — when non-empty, export uses just those. */
  selected: (string | number)[];
  /** ID extractor used when filtering by `selected`. */
  rowId?: (row: T) => string | number | undefined;
  /** Convert a parsed CSV row into a Create payload. Return null to skip. */
  rowToCreate: (row: Record<string, string>) => C | null;
  /** Bulk POST helper for the active service. */
  bulkCreate: (items: C[]) => Promise<unknown>;
  /** Refresh the list after a successful import. */
  reload: () => Promise<unknown> | void;
  /** Optional human-friendly singular noun used in toast messages. */
  noun?: string;
  /**
   * Backend module name for permission checks (e.g. "Category",
   * "Store"). When passed, the menu items are gated by the
   * `canImport` / `canExport` / `canDownloadTemplate` flags from
   * `useModulePerms`. When omitted, all three menu items always
   * render — preserves the historical behaviour for pages that opt
   * out of gating.
   */
  module?: string;
}

export interface CsvIOMenuItem {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  divider?: boolean;
}

export interface UseCsvIOResult {
  /** AddDropdown items: [Import CSV (with divider), Export CSV, Download template]. */
  menuItems: CsvIOMenuItem[];
  /** Hidden file input element — render once anywhere in the page. */
  fileInput: ReactNode;
  /** True while a bulk import is in flight. */
  importing: boolean;
}

export function useCsvIO<T, C>(config: UseCsvIOConfig<T, C>): UseCsvIOResult {
  const { filename, columns, rows, selected, rowId, rowToCreate, bulkCreate, reload, noun = 'row', module } = config;
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const csvKeys = columns.map((c) => c.header);
  // Permission gate — only applied when `module` was passed. Callers
  // that omit it (rare, legacy) get the historical unconditional set.
  const perms = useModulePerms(module ?? '*');
  const canImport = !module || perms.canImport;
  const canExport = !module || perms.canExport;
  const canTemplate = !module || perms.canDownloadTemplate;

  const handleExport = useCallback(() => {
    const source =
      selected.length > 0 && rowId
        ? rows.filter((r) => {
            const id = rowId(r);
            return id != null && selected.includes(id);
          })
        : rows;
    if (source.length === 0) {
      toast.info('Nothing to export', `Load some ${noun}s first.`);
      return;
    }
    downloadCsv(`${filename}-${Date.now()}`, rowsToCsv(source, columns));
    toast.success('Exported', `${source.length} ${noun}${source.length === 1 ? '' : 's'}`);
  }, [columns, filename, noun, rowId, rows, selected, toast]);

  const handleTemplate = useCallback(() => {
    downloadCsv(`${filename}-template`, headerOnlyCsv(columns));
    toast.success('Template downloaded', 'Fill rows then use Import CSV.');
  }, [columns, filename, toast]);

  const handleImportClick = useCallback(() => {
    fileRef.current?.click();
  }, []);

  const handleFileChosen = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      setImporting(true);
      try {
        const text = await file.text();
        const parsed = parseCsv(text, csvKeys);
        const creates: C[] = [];
        const skipped: number[] = [];
        parsed.forEach((row, i) => {
          const c = rowToCreate(row);
          if (c) creates.push(c);
          else skipped.push(i + 2); // +2 = header row + 1-indexed
        });
        if (creates.length === 0) {
          toast.error('Import failed', skipped.length ? 'Required fields missing on every row' : 'CSV had no data');
          return;
        }
        await bulkCreate(creates);
        toast.success(
          'Imported',
          `${creates.length} ${noun}${creates.length === 1 ? '' : 's'}${
            skipped.length ? ` · ${skipped.length} skipped` : ''
          }`
        );
        await reload();
      } catch (err) {
        toast.error('Import failed', err instanceof Error ? err.message : 'Failed to parse CSV');
      } finally {
        setImporting(false);
      }
    },
    [bulkCreate, csvKeys, noun, reload, rowToCreate, toast]
  );

  const menuItems: CsvIOMenuItem[] = [];
  if (canImport) {
    menuItems.push({
      divider: true,
      label: importing ? 'Importing…' : 'Import CSV',
      onClick: handleImportClick,
      icon: <Upload className="h-3.5 w-3.5" />,
      disabled: importing,
    });
  }
  if (canExport) {
    menuItems.push({
      label: selected.length > 0 ? `Export CSV (${selected.length} selected)` : 'Export CSV',
      onClick: handleExport,
      icon: <Download className="h-3.5 w-3.5" />,
    });
  }
  if (canTemplate) {
    menuItems.push({
      label: 'Download template',
      onClick: handleTemplate,
      icon: <FileDown className="h-3.5 w-3.5" />,
    });
  }

  const fileInput = (
    <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={handleFileChosen} />
  );

  return { menuItems, fileInput, importing };
}
